// src/pages/admin/components/RepairForm.js
import React, { useState, useEffect } from 'react';

function RepairForm({ repair, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    repair: '',
    price: '',
    screenTypeName: ''
  });
  const [isScreenReplacement, setIsScreenReplacement] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (repair) {
      // Clean the price to ensure it's just a number
      let cleanPrice = String(repair.price || '');
      // Remove any pound signs or other non-numeric characters
      cleanPrice = cleanPrice.replace(/[^0-9.]/g, '');

      // Check if this is a screen replacement repair (starts with "Screen Replacement -")
      const isScreenRepair = repair.repair && repair.repair.startsWith('Screen Replacement -');
      setIsScreenReplacement(isScreenRepair);

      // If it's a screen replacement, extract screen type name
      let repairName = repair.repair || '';
      let screenTypeName = '';

      if (isScreenRepair) {
        // Extract the screen type name from "Screen Replacement - [name]"
        screenTypeName = repair.repair.substring('Screen Replacement - '.length);
        repairName = repair.repair; // Keep full name for editing
      }

      setFormData({
        repair: repairName,
        price: cleanPrice,
        screenTypeName: screenTypeName
      });
    }
  }, [repair]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'price') {
      // Remove any non-numeric characters except decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

      setFormData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If editing an existing repair, just submit normally
    if (repair) {
      if (!formData.repair || !formData.price) {
        setError('Please fill in all fields');
        return;
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        setError('Please enter a valid price');
        return;
      }

      setError('');
      onSubmit({
        repair: formData.repair,
        price: formData.price
      });
      return;
    }

    // For new repairs: Check if it's a screen replacement
    if (isScreenReplacement) {
      // Validate screen type name and price
      if (!formData.screenTypeName || !formData.price) {
        setError('Please fill in screen type name and price');
        return;
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        setError('Please enter a valid price');
        return;
      }

      setError('');

      // Create repair name as "Screen Replacement - [screenTypeName]"
      onSubmit({
        repair: `Screen Replacement - ${formData.screenTypeName}`,
        price: formData.price
      });
    } else {
      // Normal repair (not screen replacement)
      if (!formData.repair || !formData.price) {
        setError('Please fill in all fields');
        return;
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        setError('Please enter a valid price');
        return;
      }

      setError('');
      onSubmit({
        repair: formData.repair,
        price: formData.price
      });
    }
  };
  
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="admin-form repair-form-modal">
        <button 
          className="modal-close-button"
          onClick={onCancel}
          type="button"
          aria-label="Close modal"
        >
          ×
        </button>
        <div className="form-header">
          <h2>{repair ? 'Edit Repair' : 'Add New Repair'}</h2>
        </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        {/* Only show Repair Type field if not a screen replacement OR if editing */}
        {(!isScreenReplacement || repair) && (
        <div className="form-group">
          <label htmlFor="repair">
            Repair Type <span className="required">*</span>
          </label>
          <input
            type="text"
            id="repair"
            name="repair"
            value={formData.repair}
            onChange={handleChange}
            required
            placeholder="e.g., Battery Replacement, Charging Port Repair"
            disabled={repair && repair.repair && repair.repair.startsWith('Screen Replacement -')}
          />
        </div>
        )}
        
        <div className="form-group">
          <label htmlFor="price">
            Price <span className="required">*</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ 
              padding: '12px', 
              backgroundColor: '#6c757d', 
              border: '1px solid #6c757d', 
              borderRight: 'none',
              color: '#fff',
              fontSize: '16px'
            }}>£</span>
            <input
              type="text"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              placeholder="99.99"
              style={{
                flex: 1,
                padding: '12px',
                border: '1px solid #6c757d',
                borderLeft: 'none',
                fontSize: '16px',
                backgroundColor: 'transparent'
              }}
            />
          </div>
          <span className="help-text">Enter price without currency symbol</span>
        </div>

        {/* Screen Replacement Checkbox */}
        {!repair && ( // Only show checkbox when adding new repair
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isScreenReplacement}
                onChange={(e) => setIsScreenReplacement(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '15px', fontWeight: '500' }}>
                This is a screen replacement repair
              </span>
            </label>
            <span className="help-text" style={{ marginTop: '5px', display: 'block' }}>
              Check this box if you want to add multiple screen type options (e.g., Premium OLED, LCD, etc.)
            </span>
          </div>
        )}

        {/* Screen Type Name - Only show if checkbox is checked AND not editing */}
        {!repair && isScreenReplacement && (
        <div className="form-group">
          <label htmlFor="screenTypeName">
            Screen Type Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="screenTypeName"
            name="screenTypeName"
            value={formData.screenTypeName}
            onChange={handleChange}
            required
            placeholder="e.g., Premium (Soft OLED), Cost Saver (LCD), Budget LCD"
          />
          <span className="help-text">
            This will create a repair named "Screen Replacement - [Screen Type Name]"
          </span>
        </div>
        )}

        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel} 
            className="btn-admin btn-admin-secondary"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-admin btn-admin-primary"
          >
            {repair ? 'Update Repair' : 'Add Repair'}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
}

export default RepairForm;